import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const BrandingContext = createContext(null);
export const useBranding = () => useContext(BrandingContext);

const DEFAULT = {
  company_name: "Sua Empresa",
  welcome_text: "Bem-vindo ao portal de pedidos",
  logo_url: "",
  primary_color: "#E05D36",
  accent_color: "#0F172A",
  owner_email: "",
  notify_on_new_order: true,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export function resolveLogoUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
}

function darken(hex, percent = 12) {
  try {
    const h = hex.replace("#", "");
    const num = parseInt(h, 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    const f = 1 - percent / 100;
    r = Math.max(0, Math.round(r * f));
    g = Math.max(0, Math.round(g * f));
    b = Math.max(0, Math.round(b * f));
    return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
  } catch { return hex; }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT);

  const applyToDom = useCallback((b) => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", b.primary_color || DEFAULT.primary_color);
    root.style.setProperty("--brand-primary-hover", darken(b.primary_color || DEFAULT.primary_color, 12));
    root.style.setProperty("--brand-accent", b.accent_color || DEFAULT.accent_color);
    document.title = b.company_name || "Portal de Pedidos";
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const merged = { ...DEFAULT, ...data };
      setBranding(merged);
      applyToDom(merged);
    } catch {
      applyToDom(DEFAULT);
    }
  }, [applyToDom]);

  useEffect(() => { load(); }, [load]);

  const update = async (partial) => {
    const { data } = await api.put("/settings", partial);
    const merged = { ...DEFAULT, ...data };
    setBranding(merged);
    applyToDom(merged);
    return merged;
  };

  return (
    <BrandingContext.Provider value={{ branding, reload: load, update }}>
      {children}
    </BrandingContext.Provider>
  );
}
